import axiosInstance from "./axiosInstance";

export interface AmenityApi {
  amenityId: number;
  publicId: string;
  code: string;
  amenityName: string;
  amenityType: "Room" | "Property";
  isActive: boolean;
  createdAt: string;
}
export interface RoomTypeApi {
  id: number;
  publicId: string;
  code: string;
  roomName: string;
  description: string;
  isActive: boolean;
}

export interface FloorApi {
  publicId: string;
  floorName: string;
  createdAt: string;
}
export interface VenueApi {
  venueId: number;
  publicId?: string;
  venueName: string;
  contactNo: string;
  emailAddress: string;
  description: string;
  timeZone: string;
  isActive: boolean;
}

export interface AllPropertyApi {
  propertyId: number;
  publicId: string;
  venueName: string;
  propertyName: string;
  isActive: boolean;
}

export interface PropertyTypeApi {
  id: number;
  publicId: string;
  name: string;
  code: string;
  isActive: boolean;
}

export interface PropertyGradeApi {
  id: number;
  publicId: string;
  name: string;
  code: string;
  isActive: boolean;
}

export interface PropertyListResponse {
  items: PropertyListApi[];
  pageNo: number;
  pageSize: number;
  totalCount: number;
}

export interface PropertyListApi {
  publicId: string;
  venueName: string;
  propertyName: string;
  propertyCode: string;
  propertyTypeId: number;
  propertyGrade: string;
  propertyTypeName: string | null;
  phoneNumber: string;
  checkInTime: string;
  checkOutTime: string;
  totalFloors: number;
  totalRooms: number;
  capacity: number;
  maxAdults: number;    
  maxChildren: number; 
  isActive: boolean;
}

export interface HotelContractPayload {
  hotelContractId?: number;
  publicId?: string;

  contractReferenceNo: string;
  contractStartDate: string;
  contractEndDate: string;
  currency: string;
  billingCycle: string;
  paymentTerms: string;
  noticePeriodDays: number;
  securityDeposit: number;
  taxPercentage: number;
  includedServices: string;
  cancellationPolicy: string;
  agreementDocumentUrl: string;

  roomTypeRents: {
    roomTypeId: number;
    rentPerRoom: number;
  }[];
}

export interface CreatePropertyPayload {
  venueId: number;
  propertyName: string;
  propertyCode: string;
  propertyGradeId: number;
  propertyTypeId: number;

  address: string;
  primaryContact: string;
  primaryEmail: string;
  phoneNumber: string;

  checkInTime: string;
  checkOutTime: string;

  earlyCheckInTime: string;
  lateCheckInTime: string;
  extraLateCheckOutTime: string;

  hotelContracts?: HotelContractPayload[];

  contacts: {
    name: string;
    label: string;
    email: string;
    phone: string;
  }[];

  amenityIds: number[];

  roomTypes: {
    roomTypeId: number;
    numberOfRooms: number;
    maxAdults: number;
    maxChildren: number;
    maxOccupancy: number;
    childAgeMax: number;
    minAdults: number;
    minOccupancy: number;
    rentPerRoom: number;
    roomNames: string[];
    amenityIds: number[];
  }[];
}

export interface PropertyStatsApi {
  totalProperties: number;
  available: number;
  totalRooms: number;
}

export interface CurrencyApi {
  currencyId: number;
  currencyCode: string;
  currencyName: string;
  currencySymbol: string;
  exchangeRate: number;
  buyRate: number | null;
  sellRate: number | null;
  isBaseCurrency: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export const getAuthHeader = () => {
  const token = localStorage.getItem("fhims_auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getAmenities = async (): Promise<AmenityApi[]> => {
  const res = await axiosInstance.get("/amenities", {
    headers: getAuthHeader(),
  });

  return res.data;
};

export const getRoomTypes = async (): Promise<RoomTypeApi[]> => {
  const res = await axiosInstance.get("/room-type", {
    headers: getAuthHeader(),
  });

  return res.data;
};

export const getFloors = async (): Promise<FloorApi[]> => {
  const res = await axiosInstance.get("/floor", {
    headers: getAuthHeader(),
  });

  return res.data;
};

export const getVenues = async (): Promise<VenueApi[]> => {
  const res = await axiosInstance.get("/Venues?onlyActive=true", {
    headers: getAuthHeader(),
  });

  return res.data;
};

export const getPropertyTypes = async (): Promise<PropertyTypeApi[]> => {
  const res = await axiosInstance.get("/property-types", {
    headers: getAuthHeader(),
  });

  return res.data;
};

export const getPropertyGrades = async (): Promise<PropertyGradeApi[]> => {
  const res = await axiosInstance.get("/property-grades", {
    headers: getAuthHeader(),
  });

  return res.data;
};

export const getProperties = async (
  pageNo: number,
  pageSize: number
): Promise<PropertyListResponse> => {
  const res = await axiosInstance.get(
    `/property/list?pageNo=${pageNo}&pageSize=${pageSize}`,
    {
      headers: getAuthHeader(),
    }
  );

  return res.data;
};

export const createProperty = async (
  payload: CreatePropertyPayload
): Promise<string> => {
  const res = await axiosInstance.post(
    "/property/create",
    payload, 
    {
      headers: getAuthHeader(),
    }
  );

  return res.data; 
};

export const updateProperty = async (
  publicId: string,
  payload: CreatePropertyPayload
): Promise<string> => {
  const res = await axiosInstance.put(
    `/property/${publicId}`,
    payload,
    {
      headers: getAuthHeader(),
    }
  );

  return res.data;
};

export const getPropertyById = async (publicId: string) => {
  const res = await axiosInstance.get(
    `/property/${publicId}`,
    {
      headers: getAuthHeader(),
    }
  );

  return res.data;
};

export const deleteProperty = async (publicId: string): Promise<void> => {
  await axiosInstance.delete(`/property/${publicId}`, {
    headers: getAuthHeader(),
  });
};

export const updatePropertyStatus = async (
  publicId: string,
  isActive: boolean
): Promise<void> => {
  await axiosInstance.patch(
    `/property/${publicId}/active`,
    { isActive },
    {
      headers: getAuthHeader(),
    }
  );
};

export const getPropertyStats = async (): Promise<PropertyStatsApi> => {
  const res = await axiosInstance.get("/property/stats", {
    headers: getAuthHeader(),
  });

  return res.data;
};

export const getCurrencies = async (): Promise<CurrencyApi[]> => {
  const res = await axiosInstance.get("/currency", {
    headers: getAuthHeader(),
  });

  return res.data;
};

export const getAllProperties = async (
  venuePublicId?: string,
): Promise<AllPropertyApi[]> => {
  const res = await axiosInstance.get("/property", {
    headers: getAuthHeader(),
    params: { venuePublicId },
  });

  return res.data;
};
